import React, { useCallback } from 'react';
import { DataGridProps, GridValidRowModel } from '@mui/x-data-grid';
import { Pagination as PaginationComponent } from '@im/ui-lib/Pagination';
import { TableSkeleton } from '@im/ui-lib/TableSkeleton';
import { DataGrid } from '../DataGrid/DataGrid';
import {
  NoRowsOverlay,
  SortedAscendingIcon,
  SortedDescendingIcon,
} from '../DataGrid';

type Pagination = {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

export interface TableProps<T extends GridValidRowModel>
  extends Omit<DataGridProps<T>, 'pagination'> {
  pagination?: Pagination;
  sort?: {
    setSort: (x: any) => void;
    sort: any;
  };
  totalCount?: number;
}

export default function Table<T extends GridValidRowModel>({
  pagination,
  sort,
  totalCount,
  ...tableOptions
}: Readonly<TableProps<T>>) {
  const requestPagination = () => {
    if (pagination) {
      return (
        <PaginationComponent
          data-testid="pagination"
          onPageChange={pagination.setPage}
          onRowsPerPageChange={pagination.setPageSize}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          totalCount={totalCount as number}
        />
      );
    } else return null;
  };
  const tableProps = {
    columnBuffer: tableOptions.columns.length,
    components: {
      ColumnSortedAscendingIcon: SortedAscendingIcon,
      ColumnSortedDescendingIcon: SortedDescendingIcon,
      LoadingOverlay: useCallback(
        () => (
          <TableSkeleton
            columns={tableOptions.columns.map(() =>
              [...Array(50)].map(() => '-').join('')
            )}
            rows={6}
          />
        ),
        [tableOptions.columns]
      ),
      NoRowsOverlay,
      Pagination: requestPagination,
    },
    ...(sort && {
      onSortModelChange: ([value]) => sort.setSort(value),
      sortModel: [sort.sort],
      sortingMode: 'server' as const,
      sortingOrder: ['asc' as const, 'desc' as const],
    }),
    hideFooterPagination: tableOptions.rows.length <= 0,
    rowBuffer: tableOptions.rows.length,
    sx: {
      '&.MuiDataGrid-root .MuiDataGrid-cell': {
        padding: '0 10px !important',
      },
      '&.MuiDataGrid-root .MuiDataGrid-columnHeader': {
        padding: '0 10px !important',
      },
      '&.MuiDataGrid-root .MuiDataGrid-columnHeaderCheckbox, &.MuiDataGrid-root .dense':
        {
          padding: '0  5px!important',
        },
    },
  } as Partial<DataGridProps<T>>;

  return (
    <DataGrid<T>
      autoHeight
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableSelectionOnClick
      hideFooterSelectedRowCount
      stickyHeader
      {...tableProps}
      {...tableOptions}
    />
  );
}
